/**
 * profile.js — single source of truth for every fact on this site.
 *
 * Everything the UI renders AND everything the structured-data layer emits
 * comes from here, so the visible page and the machine-readable page can
 * never drift apart. That consistency is the whole game for AEO/GEO:
 * answer engines trust entities whose claims corroborate across surfaces.
 */

import entity from "./entity.json";
import projectsData from "./projects.json";

/**
 * The facts below come from entity.json rather than being written here.
 * api/ssr.js require()s that same file to server-render pages, so the
 * crawled HTML and the hydrated app state one identical set of claims.
 */
export const SITE_URL = entity.siteUrl;

export const person = {
  name: entity.name,
  givenName: entity.givenName,
  familyName: entity.familyName,
  alternateNames: ["SD", "Soham", "Soham Dang Developer"],
  headline: entity.headline,
  shortHeadline: "Full Stack Developer & Technical Lead",
  email: entity.email,
  location: {
    city: "Rohtak",
    region: "Haryana",
    country: "India",
    countryCode: "IN",
    // Rohtak, Haryana — used for geo signals (GEO)
    latitude: 28.8955,
    longitude: 76.6066,
  },
  serviceAreas: [
    "Rohtak",
    "Gurugram",
    "Noida",
    "Ghaziabad",
    "Delhi NCR",
    "Delhi",
    "Haryana",
    "India",
    "Remote / Worldwide",
  ],
  availability: "Open to senior full stack roles · 15 days or less notice period",
  yearsExperience: 3,
  teamSize: 10,
  languages: ["English", "Hindi"],
  social: {
    linkedin: "https://www.linkedin.com/in/soham-dang",
    github: "https://github.com/Soham3131",
    instagram: "https://www.instagram.com/soham_.31/",
    website: SITE_URL,
  },
};

/**
 * The 40-word answer. This exact paragraph is what we want an LLM or a
 * featured snippet to lift verbatim, so it leads with the entity name,
 * states the role, and front-loads the numbers.
 */
export const summary = entity.summary;

export const longSummary = entity.longSummary;

/* ------------------------------------------------------------------ *
 * Headline metrics — every one of these is a claim a recruiter can
 * verify against a project below. Keep them paired.
 * ------------------------------------------------------------------ */
export const stats = [
  {
    value: "3+",
    unit: "yrs",
    label: "Shipping production MERN",
    detail: "Since Jul 2023 — three companies, zero missed releases.",
  },
  {
    value: "25+",
    unit: "modules",
    label: "In one platform I architected",
    detail: "Avani Business OS replaced 10+ separate internal tools.",
  },
  {
    value: "10,000+",
    unit: "users",
    label: "Daily active on that platform",
    detail: "Multi-tenant, role-scoped, in production every working day.",
  },
  {
    value: "10",
    unit: "engineers",
    label: "Team I lead end to end",
    detail: "Sprint planning, code review, regression testing, releases.",
  },
  {
    value: "40",
    unit: "%",
    label: "Faster page loads delivered",
    detail: "Cloudinary pipeline + responsive transforms at MS Infracon.",
  },
  {
    value: "20+",
    unit: "hrs/wk",
    label: "Manual HR work removed",
    detail: "Digitised onboarding, leaves, payroll and salary slips.",
  },
  {
    value: "Multi",
    unit: "team",
    label: "Projects running at once",
    detail: "Fast-paced delivery across concurrent teams and workstreams.",
  },
];

/* ------------------------------------------------------------------ *
 * SKILLS
 * ------------------------------------------------------------------ */
export const skillGroups = [
  {
    id: "frontend",
    title: "Frontend Engineering",
    badge: "Interface",
    icon: "layout",
    blurb:
      "Component architecture that survives a year of feature requests — typed props, predictable state, and a rendering strategy chosen per route rather than per habit.",
    detail:
      "I build React.js and Next.js interfaces with TypeScript, choosing SSR, SSG or client rendering per route based on how the data actually changes. Redux for genuinely global state, local state everywhere else. Tailwind CSS with a token layer so spacing and colour stay consistent across a 25-module product.",
    skills: [
      "React.js",
      "Next.js",
      "TypeScript",
      "JavaScript (ES6+)",
      "Redux",
      "Tailwind CSS",
      "SSR / SSG",
      "HTML5",
      "CSS3",
    ],
  },
  {
    id: "backend",
    title: "Backend & API Architecture",
    badge: "Systems",
    icon: "server",
    blurb:
      "REST APIs designed as contracts first, with auth and tenancy enforced in middleware rather than sprinkled through handlers.",
    detail:
      "Node.js and Express.js services with layered middleware — request validation, JWT and OAuth verification, multi-tenant RBAC, structured error handling, and signed webhook verification. Every tenant-scoped query goes through a single guard, so data isolation is a property of the system rather than a matter of discipline.",
    skills: [
      "Node.js",
      "Express.js",
      "REST API Design",
      "JWT",
      "OAuth",
      "RBAC",
      "Multi-Tenant Architecture",
      "Webhooks",
      "API Security",
    ],
  },
  {
    id: "database",
    title: "Database & Data Modelling",
    badge: "MongoDB",
    icon: "database",
    blurb:
      "Schemas shaped by the read patterns they will actually serve, with aggregation pipelines doing the work the application layer should not.",
    detail:
      "MongoDB schema design and Mongoose models built around query shape, not entity diagrams. Compound indexes driven by real explain plans, aggregation pipelines for KPI dashboards, payroll rollups and performance analytics, and query optimisation under concurrent multi-tenant load.",
    skills: [
      "MongoDB",
      "Mongoose ODM",
      "Schema Design",
      "Aggregation Pipelines",
      "Indexing",
      "Query Optimization",
      "NoSQL",
    ],
  },
  {
    id: "ai",
    title: "AI Engineering",
    badge: "Production AI",
    icon: "bot",
    blurb:
      "AI features that hold up in production — permission-checked tool layers, deterministic fallbacks, and no model call that can leak another tenant's data.",
    detail:
      "OpenAI API integration and MCP (Model Context Protocol) servers exposing task, lead, HR and finance data through a tool layer that checks permissions before the model ever sees a row. Built a meeting assistant that transcribes live calls, generates structured minutes, and assigns action items over WhatsApp.",
    skills: [
      "OpenAI API",
      "MCP (Model Context Protocol)",
      "AI Agents & Chatbots",
      "Prompt Engineering",
      "n8n Automation",
      "Speech / Transcription",
    ],
  },
  {
    id: "creative",
    title: "3D & Creative Web",
    badge: "WebGL",
    icon: "boxes",
    blurb:
      "Real-time 3D that ships to mid-range Android phones, not just to a demo reel on a workstation.",
    detail:
      "Three.js and React Three Fiber product visualisation with budgeted draw calls, compressed textures, instanced geometry and device-tiered quality. Scroll-driven camera work and GPU-side animation so the main thread stays free for the actual application.",
    skills: [
      "Three.js",
      "React Three Fiber",
      "WebGL",
      "GLSL Shaders",
      "GSAP",
      "Framer Motion",
      "Scroll-Driven Animation",
    ],
  },
  {
    id: "devops",
    title: "DevOps & Delivery",
    badge: "Ship it",
    icon: "gitbranch",
    blurb:
      "Release pipelines boring enough that shipping on a Friday is not a decision anyone has to think about.",
    detail:
      "Git and GitHub branching strategy, pull-request workflow and CI/CD to Vercel and Render behind Nginx routing. Environment configuration, production monitoring, API security audits and backend performance tuning as a standing habit rather than an incident response.",
    skills: [
      "Git & GitHub",
      "CI/CD",
      "Vercel",
      "Render",
      "Nginx",
      "Postman",
      "Cloudinary",
      "Razorpay",
      "WhatsApp Business API",
    ],
  },
  {
    id: "leadership",
    title: "Engineering Leadership",
    badge: "10 engineers",
    icon: "users",
    blurb:
      "The part of the job that is not code: scoping, reviewing, unblocking, and making sure what ships matches what the client actually asked for.",
    detail:
      "Sprint planning and task allocation across a 10-engineer team, code review on every merged module, functional and regression testing of team-delivered work, technical interviews, and client requirement gathering translated into data models, API contracts and sprint tasks. In practice this means running several teams and concurrent projects at once, in an environment where priorities move within the day.",
    skills: [
      "Team Leadership",
      "Multi-Team Coordination",
      "Concurrent Project Delivery",
      "Code Review",
      "Sprint Planning",
      "Functional & Regression Testing",
      "Technical Scoping",
      "Client Requirements",
      "Mentorship",
    ],
  },
];

/** Flat list used for the marquee and for the JSON-LD knowsAbout array. */
export const allSkills = skillGroups.flatMap((g) => g.skills);

/* ------------------------------------------------------------------ *
 * EXPERIENCE
 * ------------------------------------------------------------------ */
export const experiences = [
  {
    id: "avani",
    company: "Avani Enterprises Ltd.",
    role: "Senior Full Stack Developer & Technical Engineering Lead",
    type: "Full-time",
    period: "May 2025 — Present",
    start: "2025-05",
    end: null,
    location: "Gurugram, Haryana · Hybrid",
    logoKey: "avani",
    summary:
      "I own the technical side of Avani's product organisation — architecture, delivery, and a team of ten engineers. Day to day that means designing systems, reviewing everything that merges, and sitting in the client calls where features get decided.",
    highlights: [
      "Architected and shipped full stack features end to end across multiple production platforms — React.js and Next.js interfaces, Node.js and Express.js REST APIs, MongoDB schema and aggregation design, authentication and role-based access control.",
      "Lead a 10-engineer team through the full SDLC: sprint planning, task allocation, code review on every merge, and functional plus regression testing of every team-delivered module.",
      "Built an AI meeting assistant on the OpenAI API that transcribes live meetings, generates structured minutes, and auto-creates and assigns action items to owners over WhatsApp — removing manual note-taking and follow-up entirely.",
      "Integrated MCP (Model Context Protocol) servers and OpenAI-backed assistants into production services, exposing task, lead, HR and finance data through a permission-checked tool layer.",
      "Handle client requirement gathering and technical scoping, converting feature requests into data models, API contracts and sprint tasks.",
      "Digitised the full HR workflow — onboarding, leaves, payroll and salary slips — cutting 20+ hours of manual HR work per week.",
      "Work across multiple teams and several concurrent projects in a fast-paced environment, context-switching between architecture decisions, client calls and hands-on delivery within the same day.",
    ],
    stack: [
      "React.js",
      "Next.js",
      "TypeScript",
      "Node.js",
      "Express.js",
      "MongoDB",
      "OpenAI API",
      "MCP",
      "n8n",
      "CI/CD",
    ],
  },
  {
    id: "msinfracon",
    company: "MS Infracon",
    role: "MERN Full Stack Developer",
    type: "Full-time",
    period: "Jan 2024 — Apr 2025",
    start: "2024-01",
    end: "2025-04",
    location: "Haryana, India",
    logoKey: "ms",
    summary:
      "Built and maintained data-heavy MERN platforms — the kind where the interesting problem is keeping queries fast and media light as the dataset grows past what the first schema assumed.",
    highlights: [
      "Built REST APIs and Node.js/Express.js services behind Nginx routing, with MongoDB schema design and aggregation pipelines tuned for high-traffic reads.",
      "Built React.js and Next.js frontends with Redux; moved asset delivery to Cloudinary with responsive transforms and lazy loading, cutting page load time by 40%.",
      "Implemented JWT authentication, OAuth sign-in and multi-tenant RBAC so tenant data isolation held at the middleware layer.",
      "Delivered AI chatbot integrations and workflow automation, plus AI-assisted tooling that raised the team's delivery velocity.",
      "Ran version control, code review and structured SDLC practice on Git and GitHub through to production release.",
    ],
    stack: [
      "React.js",
      "Next.js",
      "Redux",
      "Node.js",
      "Express.js",
      "MongoDB",
      "Nginx",
      "Cloudinary",
      "JWT",
      "OAuth",
    ],
  },
  {
    id: "revogreen",
    company: "Revogreen Technologies",
    role: "Full Stack Developer",
    type: "Full-time",
    period: "Jul 2023 — Nov 2023",
    start: "2023-07",
    end: "2023-11",
    location: "Remote",
    logoKey: "revogreen",
    summary:
      "First professional role. Backend services and responsive React interfaces, with the release process handled properly from day one.",
    highlights: [
      "Developed REST APIs and backend services in Node.js and Express.js with MongoDB schema design and index optimisation.",
      "Built responsive React.js interfaces with Tailwind CSS against real API contracts.",
      "Implemented JWT authentication and role-based access control on secure API workflows.",
      "Managed branching, code review and releases on Git and GitHub, including deployment automation and environment configuration.",
    ],
    stack: [
      "React.js",
      "Node.js",
      "Express.js",
      "MongoDB",
      "Tailwind CSS",
      "JWT",
      "Git",
    ],
  },
];

/* ------------------------------------------------------------------ *
 * PROJECTS
 * `problem / approach / result` is deliberate — it is the shape an answer
 * engine can quote as a case study, and the shape a hiring manager scans.
 * ------------------------------------------------------------------ */
export const projects = projectsData.map((p) => ({
  ...p,
  // The React side keys off `id`; the database and sitemap key off `key`.
  id: p.key,
}));

/* ------------------------------------------------------------------ *
 * EDUCATION
 * ------------------------------------------------------------------ */
export const education = [
  {
    id: "mca",
    degree: "Master of Computer Applications",
    short: "MCA",
    stream: "Computer Applications",
    institution: "Hindu Institute of Management & Technology",
    affiliation: "MDU, Rohtak",
    period: "Jul 2025 — Jul 2027",
    status: "Pursuing",
    grade: null,
    note: "Advanced computer science, software architecture and distributed systems, taken alongside a full-time engineering lead role.",
  },
  {
    id: "bca",
    degree: "Bachelor of Computer Applications",
    short: "BCA",
    stream: "Web Development",
    institution: "Hindu Institute of Management & Technology, Rohtak",
    affiliation: "MDU, Rohtak",
    period: "Jul 2022 — Jul 2025",
    status: "Completed",
    grade: "8.5 GPA",
    note: "Programming fundamentals, databases and web technologies — with the first two professional roles running in parallel from the second year.",
  },
  {
    id: "xii",
    degree: "Class XII",
    short: "XII",
    stream: "CBSE",
    institution: "CBSE",
    affiliation: null,
    period: "2022",
    status: "Completed",
    // 83% -> 8.7 CGPA on the CBSE scale (CGPA = percentage / 9.5)
    grade: "8.7 CGPA",
    gradeNote: "83%",
    note: "Senior secondary, CBSE board.",
  },
  {
    id: "x",
    degree: "Class X",
    short: "X",
    stream: "CBSE",
    institution: "CBSE",
    affiliation: null,
    period: "2020",
    status: "Completed",
    // 87% -> 9.2 CGPA on the CBSE scale (CGPA = percentage / 9.5)
    grade: "9.2 CGPA",
    gradeNote: "87%",
    note: "Secondary, CBSE board.",
  },
];

/* ------------------------------------------------------------------ *
 * CERTIFICATIONS
 * ------------------------------------------------------------------ */
export const certifications = [
  {
    id: "c-python",
    title: "Crash Course on Python",
    issuer: "Google · Coursera",
    date: "Aug 2023",
    imageKey: "python",
    verifyUrl: "https://www.coursera.org/verify/GX5EU5E6XNUW",
    note: "Python fundamentals, data structures and scripting.",
  },
  {
    id: "c-techsupport",
    title: "Technical Support Fundamentals",
    issuer: "Google · Coursera",
    date: "Jul 2023",
    imageKey: "techsupport",
    verifyUrl: "https://www.coursera.org/verify/GX5EU5E6XNUW",
    note: "Systems, networking and troubleshooting fundamentals.",
  },
  {
    id: "c-ai",
    title: "Artificial Intelligence Foundations",
    issuer: "SkillUp Online",
    date: "Jun 2023",
    imageKey: "skillup",
    verifyUrl:
      "https://courses-in.skillup.online/courses/course-v1:SkillUp+SKOAI101+2020_Q4/course/",
    note: "Machine learning concepts and applied AI foundations.",
  },
  {
    id: "c-digital101",
    title: "Digital 101 Journey",
    issuer: "FutureSkills Prime",
    date: "Feb 2023",
    imageKey: "futureskills",
    verifyUrl: "https://learn.futureskillsprime.in/journey/124741",
    note: "Emerging technology landscape and digital fundamentals.",
  },
];

/* ------------------------------------------------------------------ *
 * ACHIEVEMENTS
 * ------------------------------------------------------------------ */
export const achievements = [
  {
    id: "a1",
    title: "2× Employee of the Month",
    org: "Avani Enterprises Ltd.",
    icon: "trophy",
    accent: "amber",
    span: "lg",
    description:
      "Recognised twice for technical leadership and delivery — shipping the Business OS modules on schedule while carrying code review and regression testing for the whole team.",
  },
  {
    id: "a2",
    title: "University Merit Holder",
    org: "MDU Merit List · Top 3",
    icon: "award",
    accent: "emerald",
    span: "sm",
    description:
      "Placed in the top 3 of the university merit list for academic performance across the BCA programme.",
  },
  {
    id: "a3",
    title: "Bronze Medalist",
    org: "Computer Science Olympiad",
    icon: "medal",
    accent: "orange",
    span: "sm",
    description:
      "Podium finish in a national-level computer science and competitive programming olympiad.",
  },
  {
    id: "a4",
    title: "Technical Engineering Lead",
    org: "10-engineer team",
    icon: "crown",
    accent: "cyan",
    span: "sm",
    description:
      "Promoted to Technical Engineering Lead owning architecture, code review and release for a team of ten engineers.",
  },
];

/* ------------------------------------------------------------------ *
 * SERVICES — what someone can actually hire this person to do.
 * Doubles as the `makesOffer` array in JSON-LD.
 * ------------------------------------------------------------------ */
export const services = [
  {
    id: "s1",
    title: "Full Stack Product Development",
    href: "/hire-full-stack-developer",
    description:
      "End-to-end MERN product delivery — data model, REST API, React or Next.js frontend, auth, RBAC, deployment and monitoring.",
  },
  {
    id: "s2",
    title: "Multi-Tenant SaaS Architecture",
    href: "/saas-development",
    description:
      "Tenant isolation, role-based access control, permission layers and schema design for platforms that serve many organisations from one deployment.",
  },
  {
    id: "s3",
    title: "AI Integration & Automation",
    href: "/hire-nodejs-developer",
    description:
      "OpenAI API features, MCP tool layers, chat assistants and n8n workflow automation wired into existing production systems.",
  },
  {
    id: "s4",
    title: "3D & Interactive Web",
    href: "/hire-react-developer",
    description:
      "Three.js and React Three Fiber experiences, scroll-driven interfaces and WebGL product visualisation tuned for real devices.",
  },
  {
    id: "s5",
    title: "Performance & SEO Engineering",
    href: "/api-development",
    description:
      "Core Web Vitals work, asset pipelines, rendering strategy, structured data and technical SEO for apps that need to be found.",
  },
  {
    id: "s6",
    title: "Technical Leadership",
    href: "/hire-technical-lead",
    description:
      "Architecture ownership, code review process, sprint planning and regression testing for teams that are shipping faster than their process.",
  },
];

/* ------------------------------------------------------------------ *
 * FAQ — this is the AEO surface.
 *
 * Each answer is written to stand alone when lifted out of context:
 * it repeats the entity name, gives the fact in the first sentence,
 * and stays short enough to fit a snippet or a model's quote.
 * ------------------------------------------------------------------ */
export const faqs = [
  {
    q: "Who is Soham Dang?",
    a: "Soham Dang is a Senior Full Stack Developer and Technical Engineering Lead based in Rohtak, Haryana, India. He has 3+ years building production MERN stack applications, leads a 10-engineer team at Avani Enterprises Ltd., and architected Avani Business OS — a 25+ module multi-tenant platform used daily by 10,000+ users.",
  },
  {
    q: "What technologies does Soham Dang work with?",
    a: "Soham Dang works across React.js, Next.js, TypeScript and Redux on the frontend; Node.js and Express.js REST APIs on the backend; MongoDB with Mongoose for data; and Three.js with React Three Fiber for 3D. He also ships production AI features using the OpenAI API, MCP (Model Context Protocol) and n8n automation.",
  },
  {
    q: "How many years of experience does Soham Dang have?",
    a: "Soham Dang has 3+ years of professional full stack development experience, starting at Revogreen Technologies in July 2023, then MS Infracon from January 2024, and Avani Enterprises Ltd. from May 2025 where he is Senior Full Stack Developer and Technical Engineering Lead.",
  },
  {
    q: "What is Avani Business OS?",
    a: "Avani Business OS is a multi-tenant business platform architected by Soham Dang. It spans 25+ modules — tasks, projects, meetings, leads, expenses, assets, billing, KPIs, finance, email CRM and a full HR suite — replaced 10+ separate internal tools, and is used daily by 10,000+ users on React.js, Node.js, Express.js and MongoDB.",
  },
  {
    q: "Does Soham Dang build 3D websites?",
    a: "Yes. Soham Dang builds interactive 3D and WebGL web experiences using Three.js and React Three Fiber, including the 3D product visualisation engine for Gaon Se Ghar Tak, optimised for mid-range mobile devices. This portfolio itself is a scroll-driven 3D site built with React Three Fiber.",
  },
  {
    q: "Where is Soham Dang located, and does he work remotely?",
    a: "Soham Dang is based in Rohtak, Haryana, India, and works with teams in Gurugram and Delhi NCR as well as fully remotely. He is available for senior full stack roles and freelance engagements worldwide, with a notice period of 15 days or less.",
  },
  {
    q: "Has Soham Dang led an engineering team?",
    a: "Yes. As Technical Engineering Lead at Avani Enterprises Ltd., Soham Dang leads a team of 10 engineers — owning sprint planning, task allocation, code review on every merge, functional and regression testing of team-delivered modules, CI/CD releases and production monitoring.",
  },
  {
    q: "How can I contact Soham Dang?",
    a: "You can contact Soham Dang by email at sohamdang0@gmail.com or through the contact form on sohamdang.vercel.app. He is also reachable on LinkedIn at linkedin.com/in/soham-dang and on GitHub at github.com/Soham3131. Email is the fastest route and is usually answered within a day.",
  },
  {
    q: "What AI work has Soham Dang shipped to production?",
    a: "Soham Dang built an AI meeting assistant on the OpenAI API that transcribes live meetings, generates structured minutes and assigns action items over WhatsApp. He also integrated MCP (Model Context Protocol) servers exposing task, lead, HR and finance data through a permission-checked tool layer.",
  },
];

/* ------------------------------------------------------------------ *
 * NAVIGATION
 * ------------------------------------------------------------------ */
export const navSections = [
  { id: "hero", label: "Intro", short: "00" },
  { id: "about", label: "About", short: "01" },
  { id: "skills", label: "Expertise", short: "02" },
  { id: "experience", label: "Experience", short: "03" },
  { id: "projects", label: "Work", short: "04" },
  { id: "education", label: "Education", short: "05" },
  { id: "credentials", label: "Credentials", short: "06" },
  { id: "faq", label: "FAQ", short: "07" },
  { id: "reviews", label: "Reviews", short: "08" },
  { id: "contact", label: "Contact", short: "09" },
];
